import { useState, useEffect, useCallback } from 'react';
import {
  MapContainer,
  TileLayer,
  Marker,
  Popup,
  useMapEvents,
  useMap,
} from 'react-leaflet';
import { ISLANDS, ISLAND_BY_CODE, getCityCoords } from '../data/caribbeanCities';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Fix default icon paths broken by webpack
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

const biteIcon = L.divIcon({
  className: '',
  html: `<div class="bite-icon">🦟</div>`,
  iconSize: [32, 32],
  iconAnchor: [16, 16],
  popupAnchor: [0, -16],
});

const CENTER = [16.5, -63.5];
const ZOOM = 6;

function ClickHandler({ onMapClick }) {
  useMapEvents({
    click(e) {
      onMapClick(e.latlng.lat, e.latlng.lng);
    },
  });
  return null;
}

// Smoothly pans the map whenever `target` changes
function MapAutoFly({ target }) {
  const map = useMap();
  useEffect(() => {
    if (target) {
      map.flyTo([target.lat, target.lng], target.zoom, { duration: 1.5 });
    }
  }, [target, map]);
  return null;
}

function BiteModal({ latlng, ipGeo, onConfirm, onCancel, onFlyTo }) {
  const defaultCode =
    ipGeo?.country_code && ISLAND_BY_CODE[ipGeo.country_code]
      ? ipGeo.country_code
      : ISLANDS[0].code;

  const [countryCode, setCountryCode] = useState(defaultCode);
  const [city, setCity] = useState('');
  const island = ISLAND_BY_CODE[countryCode] || ISLANDS[0];

  // Reset city when territory changes
  useEffect(() => { setCity(''); }, [countryCode]);

  // Fly to city coords instantly from local data whenever a city is chosen
  useEffect(() => {
    if (!city || !latlng) return;
    const coords = getCityCoords(island, city);
    if (coords) onFlyTo(coords);
  }, [city, island, latlng, onFlyTo]);

  if (!latlng) return null;

  const cityCoords = city ? getCityCoords(island, city) : null;

  return (
    <div className="modal-overlay" onClick={onCancel}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <h3>Piqûre signalée 🦟</h3>

        <label className="modal-label">Île / Territoire</label>
        <select
          className="modal-select"
          value={countryCode}
          onChange={(e) => setCountryCode(e.target.value)}
        >
          {ISLANDS.map((i) => (
            <option key={i.code} value={i.code}>{i.name}</option>
          ))}
        </select>

        <label className="modal-label">Ville</label>
        <select
          className="modal-select"
          value={city}
          onChange={(e) => setCity(e.target.value)}
          autoFocus
        >
          <option value="">— Sélectionnez une ville —</option>
          {island.cities.map((c) => (
            <option key={c.name} value={c.name}>{c.name}</option>
          ))}
        </select>

        {cityCoords && (
          <p className="modal-geo-status modal-geo-ok">
            📍 {cityCoords.lat.toFixed(4)}, {cityCoords.lng.toFixed(4)}
          </p>
        )}

        <div className="modal-actions">
          <button className="btn-cancel" onClick={onCancel}>Annuler</button>
          <button
            className="btn-confirm"
            onClick={() => onConfirm({ city, country: island.name, countryCode, cityCoords })}
            disabled={!city}
          >
            Enregistrer
          </button>
        </div>
      </div>
    </div>
  );
}

export default function CaribbeanMap({ bites, addBite, removeBite, ipGeo }) {
  const [pending, setPending] = useState(null);
  const [flyTarget, setFlyTarget] = useState(null);

  // Fly to detected island when IP geo loads
  useEffect(() => {
    if (ipGeo) {
      const island = ISLAND_BY_CODE[ipGeo.country_code];
      if (island) {
        setFlyTarget({ lat: island.center[0], lng: island.center[1], zoom: island.zoom, ts: Date.now() });
      }
    }
  }, [ipGeo]);

  const handleMapClick = (lat, lng) => {
    setPending({ lat, lng });
    // Re-fly to the detected island when the dialog opens
    if (ipGeo) {
      const island = ISLAND_BY_CODE[ipGeo.country_code];
      if (island) {
        setFlyTarget({ lat: island.center[0], lng: island.center[1], zoom: island.zoom, ts: Date.now() });
      }
    }
  };

  const handleFlyTo = useCallback(({ lat, lng }) => {
    setFlyTarget({ lat, lng, zoom: 13, ts: Date.now() });
  }, []);

  const handleConfirm = ({ city, country, countryCode, cityCoords }) => {
    // Use geocoded city coords; fall back to island center if geocoding failed
    const island = ISLAND_BY_CODE[countryCode];
    const fallback = island
      ? { lat: island.center[0], lng: island.center[1] }
      : { lat: pending.lat, lng: pending.lng };
    const coords = cityCoords || fallback;
    addBite({
      lat: coords.lat,
      lng: coords.lng,
      city,
      country,
      countryCode,
      ip: ipGeo?.ip || null,
    });
    setPending(null);
  };

  return (
    <div className="map-wrapper">
      <MapContainer
        center={CENTER}
        zoom={ZOOM}
        style={{ height: '100%', width: '100%' }}
        minZoom={4}
        maxZoom={16}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        <ClickHandler onMapClick={handleMapClick} />
        <MapAutoFly target={flyTarget} />
        {bites.map((bite) => (
          <Marker key={bite.id} position={[bite.lat, bite.lng]} icon={biteIcon}>
            <Popup>
              <div className="bite-popup">
                <strong>🦟 Ti-yenyen</strong>
                {bite.city && (
                  <div className="bite-city">{bite.city}, {bite.country}</div>
                )}
                <div className="bite-time">
                  {new Date(bite.timestamp || Date.now()).toLocaleTimeString('fr-FR', {
                    hour: '2-digit', minute: '2-digit',
                  })}
                </div>
                {bite.ip && <div className="bite-ip">IP : {bite.ip}</div>}
                <button
                  className="btn-delete"
                  onClick={() => removeBite(bite.id)}
                >
                  Supprimer
                </button>
              </div>
            </Popup>
          </Marker>
        ))}
      </MapContainer>

      <BiteModal
        latlng={pending}
        ipGeo={ipGeo}
        onConfirm={handleConfirm}
        onCancel={() => setPending(null)}
        onFlyTo={handleFlyTo}
      />

      <div className="map-hint">
        Cliquez sur la carte pour signaler une piqûre
      </div>
    </div>
  );
}
